import axios from "axios";
import { logger } from "./logger";
import { Global } from "../constants/global";
import { RequestHeader } from "../constants/requestHeader";
import { formatTimestamps } from "./dateUtil";
import { AppConfig } from "../constants/appConfig";

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

const rewriteLocalhostUrls = (obj) => {
  if (typeof obj === "string") {
    return obj.replace(/http:\/\/localhost:\d+/g, AppConfig.ENV.API_URL);
  }
  if (Array.isArray(obj)) {
    return obj.map(rewriteLocalhostUrls);
  }
  if (typeof obj === "object" && obj !== null) {
    const newObj = {};
    for (const key in obj) {
      newObj[key] = rewriteLocalhostUrls(obj[key]);
    }
    return newObj;
  }
  return obj;
};

const IGNORED_AUTH_PATHS = [
  "/login",
  "/signup",
  "/refresh",
  "/logout",
  "/forgot-password",
  "/reset-password",
];

const createApiClient = (baseURL, options) => {
  const { projectId, refreshUrl, extractMeta = false, onLatency } = options;

  const client = axios.create({
    baseURL,
    timeout: 30000,
    withCredentials: true,
    headers: {
      [RequestHeader.CONTENT_TYPE]: "application/json",
      [RequestHeader.X_PROJECT]: projectId,
      [RequestHeader.ACCEPT]: "application/json",
      [RequestHeader.X_REQUESTED_WITH]: "XMLHttpRequest",
      [RequestHeader.CACHE_CONTROL]: "no-cache, no-store, must-revalidate",
      [RequestHeader.PRAGMA]: "no-cache",
      [RequestHeader.EXPIRES]: "0",
    },
  });

  client.interceptors.request.use(
    (config) => {
      const startTime = performance.now();
      config.metadata = { startTime };

      config.headers[RequestHeader.X_PROJECT] = projectId;

      if (typeof FormData !== "undefined" && config.data instanceof FormData) {
        if (config.headers && typeof config.headers.delete === "function") {
          config.headers.delete(RequestHeader.CONTENT_TYPE);
        } else {
          delete config.headers[RequestHeader.CONTENT_TYPE];
        }
      } else if (config.data) {
        config.data = formatTimestamps(config.data);
      }

      logger.apiRequest(config.method?.toUpperCase(), config.url, config.data);
      return config;
    },
    (error) => {
      logger.error("API Request Error:", error);
      return Promise.reject(error);
    },
  );

  client.interceptors.response.use(
    (response) => {
      const endTime = performance.now();
      const duration =
        endTime - (response.config.metadata?.startTime || endTime);

      if (response.data && response.config.responseType !== "blob") {
        response.data = rewriteLocalhostUrls(formatTimestamps(response.data));
      }

      logger.apiResponse(
        response.config.method?.toUpperCase(),
        response.config.url,
        response,
        duration.toFixed(2),
      );

      if (typeof onLatency === "function") {
        onLatency(duration, response.config);
      }

      if (response.config.responseType === "blob") {
        if (response.config.returnFullBlobResponse) {
          return response;
        }
        return response.data;
      }

      const { success, data, message, ...meta } = response.data || {};

      if (success === false) {
        const error = new Error(message || "Operation failed");
        error.status = response.status;
        error.data = data;
        return Promise.reject(error);
      }

      const result = data !== undefined ? data : response.data;

      if (
        extractMeta &&
        result &&
        typeof result === "object" &&
        Object.keys(meta).length > 0
      ) {
        result._meta = meta;
      }

      return result;
    },
    async (error) => {
      const originalRequest = error.config;
      const endTime = performance.now();
      const duration =
        originalRequest && originalRequest.metadata
          ? endTime - originalRequest.metadata.startTime
          : 0;

      const apiMessage = error.response?.data?.message || error.message;
      error.message = apiMessage;
      if (error.response?.data?.data) {
        error.data = error.response.data.data;
      }

      if (
        error.response?.status === 401 &&
        originalRequest &&
        !originalRequest._retry
      ) {
        const isIgnoredPath = IGNORED_AUTH_PATHS.some((path) =>
          originalRequest.url?.includes(path),
        );

        if (isIgnoredPath) {
          return Promise.reject(error);
        }

        if (isRefreshing) {
          return new Promise(function (resolve, reject) {
            failedQueue.push({ resolve, reject });
          })
            .then(() => {
              return client(originalRequest);
            })
            .catch((err) => {
              return Promise.reject(err);
            });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          const res = await axios.post(
            `${AppConfig.ENV.API_URL}${refreshUrl}`,
            {},
            {
              withCredentials: true,
              headers: { [RequestHeader.X_PROJECT]: projectId },
            },
          );

          if (res.data?.success || res.status === 200) {
            processQueue(null);
            return client(originalRequest);
          } else {
            throw new Error("Refresh response unsuccessful");
          }
        } catch (err) {
          processQueue(err);

          if (typeof window !== "undefined") {
            if (typeof localStorage !== "undefined") {
              const keysToRemove = Object.keys(localStorage).filter(
                (key) =>
                  key.includes("token") ||
                  key.includes("auth") ||
                  key.includes("user"),
              );
              keysToRemove.forEach((key) => localStorage.removeItem(key));
            }
            window.dispatchEvent(new CustomEvent("authRequired"));
          }

          return Promise.reject(err);
        } finally {
          isRefreshing = false;
        }
      }

      logger.error("API Response Error:", {
        url: originalRequest?.url,
        method: originalRequest?.method,
        status: error.response?.status,
        message: apiMessage,
        duration: duration.toFixed(2),
      });

      return Promise.reject(error);
    },
  );

  return client;
};

const apiClient = createApiClient(AppConfig.ENV.API_URL, {
  projectId: Global.JIGENPASSB2B,
  refreshUrl: "/auth/b2b/refresh",
});

export default apiClient;
